<template>
  <div
    class="bowtie-container"
    data-test="bowtie-container"
  >
    <div
      ref="bowtieTarget"
      class="bowtie-target"
    >
      <div class="bowtie-column selected-answers column-1">
        <div
          v-for="answer in selectedActionAnswers"
          :key="answer.id"
          class="answer-target answer-target-selected"
          tabindex="0"
          @click="deselectAnswer(answer)"
          @keydown.space.self="deselectAnswer(answer)"
          @keydown.enter.self="deselectAnswer(answer)"
        >
          <span>{{ answer.text }}</span>
        </div>
        <div
          v-if="selectedActionAnswers.length === 0"
          class="answer-target"
        >
          Actions to Take
        </div>
        <div
          v-if="selectedActionAnswers.length <= 1"
          class="answer-target"
        >
          Actions to Take
        </div>
      </div>
      <div class="bowtie-column selected-answers column-2">
        <div
          v-if="selectedConditionAnswers.length === 0"
          class="answer-target"
        >
          Condition Most Likely Experiencing
        </div>
        <div
          v-for="answer in selectedConditionAnswers"
          :key="answer.id"
          class="answer-target answer-target-selected"
          tabindex="0"
          @click="deselectAnswer(answer)"
          @keydown.space.self="deselectAnswer(answer)"
          @keydown.enter.self="deselectAnswer(answer)"
        >
          <span>{{ answer.text }}</span>
        </div>
      </div>
      <div class="bowtie-column selected-answers column-3">
        <div
          v-for="answer in selectedParameterAnswers"
          :key="answer.id"
          class="answer-target answer-target-selected"
          tabindex="0"
          @click="deselectAnswer(answer)"
          @keydown.space.self="deselectAnswer(answer)"
          @keydown.enter.self="deselectAnswer(answer)"
        >
          <span>{{ answer.text }}</span>
        </div>
        <div
          v-if="selectedParameterAnswers.length === 0"
          class="answer-target"
        >
          Parameters to Monitor
        </div>
        <div
          v-if="selectedParameterAnswers.length <= 1"
          class="answer-target"
        >
          Parameters to Monitor
        </div>
      </div>
      <div class="bowtie-column column-1 choice-columns">
        <div class="column-header">
          Actions to Take
        </div>
        <div
          class="answer-wrapper"
        >
          <div
            v-for="answer in actionAnswers"
            :key="answer.id"
            class="answer-choice"
            :class="{ 'answer-choice-selected': answer.selected }"
            :tabindex="answer.selected ? -1: 0"
            :data-test="`answer-choice-${answer.id}`"
            @click="selectAnswer(answer)"
            @keydown.space.self="selectAnswer(answer)"
            @keydown.enter.self="selectAnswer(answer)"
          >
            <span>{{ answer.text }}</span>
          </div>
        </div>
      </div>
      <div class="bowtie-column column-2 choice-columns">
        <div class="column-header">
          Potential Conditions
        </div>
        <div
          class="answer-wrapper"
        >
          <div
            v-for="answer in conditionAnswers"
            :key="answer.id"
            class="answer-choice"
            :class="{ 'answer-choice-selected': answer.selected }"
            :tabindex="answer.selected ? -1: 0"
            :data-test="`answer-choice-${answer.id}`"
            @click="selectAnswer(answer)"
            @keydown.space.self="selectAnswer(answer)"
            @keydown.enter.self="selectAnswer(answer)"
          >
            <span>{{ answer.text }}</span>
          </div>
        </div>
      </div>
      <div class="bowtie-column column-3 choice-columns">
        <div class="column-header">
          Parameters to Monitor
        </div>
        <div
          class="answer-wrapper"
        >
          <div
            v-for="answer in parameterAnswers"
            :key="answer.id"
            class="answer-choice"
            :class="{ 'answer-choice-selected': answer.selected }"
            :tabindex="answer.selected ? -1: 0"
            :data-test="`answer-choice-${answer.id}`"
            @click="selectAnswer(answer)"
            @keydown.space.self="selectAnswer(answer)"
            @keydown.enter.self="selectAnswer(answer)"
          >
            <span>{{ answer.text }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

const ACTIONS_COLUMN = 1;
const CONDITIONS_COLUMN = 2;
const PARAMETERS_COLUMN = 3;

export default {
  name: 'Bowtie',
  props: {
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    flashcardState: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['answers-selected'],
  data() {
    return {
      selectedAnswers: [],
    };
  },
  computed: {
    answerIds() {
      return this.flashcardState?.answerIds || [];
    },
    answerChoices() {
      return [...this.flashcard.answers]
        .map(a => ({...a, selected: this.answerIds.includes(a.id)}));
    },
    actionAnswers() {
      return this.answerChoices.filter(a => a.dropDownIdentifier === ACTIONS_COLUMN);
    },
    selectedActionAnswers() {
      return this.selectedAnswers.filter(a => a.dropDownIdentifier === ACTIONS_COLUMN);
    },
    conditionAnswers() {
      return this.answerChoices.filter(a => a.dropDownIdentifier === CONDITIONS_COLUMN);
    },
    selectedConditionAnswers() {
      return this.selectedAnswers.filter(a => a.dropDownIdentifier === CONDITIONS_COLUMN);
    },
    parameterAnswers() {
      return this.answerChoices.filter(a => a.dropDownIdentifier === PARAMETERS_COLUMN);
    },
    selectedParameterAnswers() {
      return this.selectedAnswers.filter(a => a.dropDownIdentifier === PARAMETERS_COLUMN);
    },
  },
  watch: {
    answerIds(newAnswerIds) {
      this.selectedAnswers = newAnswerIds.map(id => this.flashcard.answers.find(a => a.id === id));
    },
    selectedAnswers(newSelectedAnswers) {
      this.$emit('answers-selected', newSelectedAnswers.map((a) => a.id));
    },
    flashcard(newFlashcard) {
      this.selectedAnswers = this.flashcardState?.answerIds
        ? newFlashcard.answers.filter((a) =>
            this.flashcardState.answerIds.includes(a.id),
          )
        : [];
    },
  },
  mounted() {
    this.selectedAnswers = this.flashcardState?.answerIds
      ? this.flashcard.answers.filter((a) =>
          this.flashcardState.answerIds.includes(a.id),
        )
      : [];
  },
  methods: {
    selectAnswer(answer) {
      if (!this.canSelectAnswer(answer)) return;
      if (this.isAnswerSelected(answer)) return;
      this.selectedAnswers = [...new Set([...this.selectedAnswers, answer])];
    },
    deselectAnswer(answer) {
      this.selectedAnswers = this.selectedAnswers.filter(a => a.id !== answer.id);
    },
    isAnswerSelected(answer) {
      return this.selectedAnswers.some(a => a.id === answer.id);
    },
    canSelectAnswer(answer) {
      if (this.selectedAnswers.includes(answer)) {
        return false;
      }

      if (
        answer.dropDownIdentifier === ACTIONS_COLUMN
        && this.selectedActionAnswers.length >= 2
      ) {
        return false;
      }

      if (
        answer.dropDownIdentifier === CONDITIONS_COLUMN
        && this.selectedConditionAnswers.length >= 1
      ) {
        return false;
      }

      if (
        answer.dropDownIdentifier === PARAMETERS_COLUMN
        && this.selectedParameterAnswers.length >= 2
      ) {
        return false;
      }

      return true;
    },
  },
};
</script>

<style lang="scss" scoped>

.bowtie-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 773px;
  margin-bottom: 3rem;

  .answer-choice {
    cursor: pointer;
  }

  .bowtie-target {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: minmax(0px, 1fr);
    scroll-snap-type: x mandatory;
    scroll-snap-align: center;
    overflow-x: auto;
    gap: 1rem;
    row-gap: 3rem;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }

    .bowtie-column {
      display: flex;
      flex-direction: column;
      justify-content: center;
      scroll-snap-align: center;
      scroll-snap-stop: always;

      &.selected-answers {
        gap: 1rem;
      }

      & > * {
        border: 1px solid black;

        &.answer-wrapper {
          display: flex;
          flex-direction: column;
          padding: 0.25rem;
          border-top: none;
          gap: 0.5rem;
        }
      }

      &.choice-columns {
        justify-content: start;

        .answer-choice {
          display: flex;
          align-items: center;
          justify-content: left;
          min-height: 4rem;
          text-align: left;
          padding: 0.5rem 1rem;
          border-radius: 2px;
          gap: 1rem;

          &.answer-choice-selected {
            opacity: 0.0;
            cursor: default;
          }
        }

      &.column-1 {
        .answer-choice {
          background: #A1D3AD;

          &:hover {
            background: #A1D3AD;
            box-shadow: 0px 1px 6px rgba(161, 211, 173, 0.4);
          }
        }
      }

      &.column-2 {
        .answer-choice {
          background: #E3F4FE;

          &:hover {
            background: #E3F4FE;
            box-shadow: 0px 1px 6px rgba(227, 244, 254, 0.4);
          }
        }
      }

      &.column-3 {
        .answer-choice {
          background: #A0DAF9;

          &:hover {
            background: #A0DAF9;
            box-shadow: 0px 1px 6px rgba(160, 218, 249, 0.4);
          }
        }
      }
      }

      .answer-target {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 4rem;
        text-align: center;
        border-radius: 2px;
        font-size: 1rem;
        padding: 0.5rem 1rem;
        font-weight: 600;
        gap: 1rem;
      }

      &.column-1 {
        .answer-target {
          background: #A1D3AD;
          border: 1px solid rgba(98, 145, 109, 0.3);
          &.answer-target-selected {
            border: 1px solid #8EBF9A;
            justify-content: space-between;

            &:hover {
              background: #8FB999;
              border: 1px solid #668B6F;
              box-shadow: 0px 1px 6px rgba(143, 185, 153, 0.4);
            }
          }
        }
      }

      &.column-2 {
        .answer-target {
          background: #E3F4FE;
          border: 1px solid rgba(132, 163, 181, 0.3);
          &.answer-target-selected {
            border: 1px solid #C7DCE8;

            &:hover {
              background: #CEE8F7;
              border: 1px solid #B4D4E7;
              box-shadow: 0px 1px 6px rgba(206, 232, 247, 0.4);
            }
          }
        }

      }

      &.column-3 {
        .answer-target {
          background: #A0DAF9;
          border: 1px solid rgba(77, 113, 132, 0.3);

          &.answer-target-selected {
            border: 1px solid #C7DCE8;

            &:hover {
              background: #85C9ED;
              border: 1px solid #69A0BE;
              box-shadow: 0px 1px 6px rgba(160, 218, 249, 0.4);
            }
          }
        }
      }
      .answer-target-selected {
        font-size: 1rem;
        text-align: left;
        justify-content: left;
        cursor: pointer;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .bowtie-container {
    left: 0;
    width: 100%;
    .bowtie-target {
      row-gap: 1.5rem;

      .bowtie-column {
        width: 247px;
      }
    }
  }
}

.column-header {
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  padding: 1rem;
}
</style>
